import { AxiosError } from 'axios';

import { t } from '@/i18n';
import { AxiosBackendError } from '@/models/api';

const AXIOS_BACKEND_ERROR_UNKNOWN: AxiosBackendError = {
    code: 418,
    message: 'UnknownError',
    trace: [],
};

const AXIOS_BACKEND_ERROR_NETWORK: AxiosBackendError = {
    code: 421,
    message: 'NetworkError',
    trace: [],
};

export function getAxiosBackendError(axiosError: unknown | AxiosError, strict?: false): AxiosBackendError | undefined;
export function getAxiosBackendError(axiosError: unknown | AxiosError, strict: true): AxiosBackendError;
export function getAxiosBackendError(axiosError: unknown | AxiosError, strict = false): AxiosBackendError | undefined {
    if (!axiosError) return strict ? AXIOS_BACKEND_ERROR_UNKNOWN : undefined;

    if ('code' in (axiosError as AxiosError) && (axiosError as AxiosError).code === 'ERR_NETWORK') {
        return AXIOS_BACKEND_ERROR_NETWORK;
    }

    const backendError = (axiosError as AxiosError)?.response?.data;
    if (backendError === undefined) {
        return strict ? AXIOS_BACKEND_ERROR_UNKNOWN : undefined;
    }

    if (typeof backendError === 'object' && backendError !== null && 'config' in backendError) {
        delete backendError.config;
    }

    return backendError as AxiosBackendError;
}

export function sanitizeErrorMessage(message?: string) {
    if (!message) return t('defaultErrorTryAgainLater', { ns: 'errors' });

    // NOTE: clean ':' and 'API\PHP\Instance\Valid Message'
    let errorMessage = message.replace(/:/g, '').split('\\').pop()!;

    // NOTE: invalid-uuid error message
    if (errorMessage.includes('Invalid UUID string')) {
        const uuid = errorMessage
            .slice(errorMessage.indexOf('Invalid UUID string') + 20)
            .trim()
            .split(' ')
            .shift();
        const sanitizeKey = errorMessage.replace(` ${uuid}`, '').replace(/\.$/, '');
        // @ts-expect-error unknown-string become from api
        errorMessage = `${t(`${sanitizeKey}`, { uuid, ns: 'errors' })}`;
    }

    // NOTE: not-found error message
    if (errorMessage.includes('was not found')) {
        const uuid = errorMessage.slice(0, errorMessage.indexOf('was not found')).trim().split(' ').pop();
        const sanitizeKey = errorMessage.replace(`${uuid} `, '').replace(/\.$/, '');
        // @ts-expect-error unknown-string become from api
        errorMessage = t(`${sanitizeKey}`, { uuid, ns: 'errors' });
    }

    // @ts-expect-error unknown-string become from api
    return t(`${errorMessage}`, { ns: 'errors' }) as string;
}

export function sanitizeApiErrorMessage(apiError?: AxiosBackendError, { sep = ' ' }: { sep?: string } = {}) {
    if (!apiError) return [];

    const messages: string[] = [];

    if (Object.keys(apiError.errors ?? {}).length) {
        Object.entries(apiError.errors!).forEach(([key, value]) => {
            // Try to translate error key if possible
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const field = t(`${key}` as any);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const error = t(`${value}` as any, { ns: 'errors' });
            messages.push(sanitizeErrorMessage(`${field}: ${error}`));
        });
    } else {
        messages.push(sanitizeErrorMessage(apiError.message));
    }

    return messages.join(sep);
}
